/**
 * MyCopro - Admin Menu Component
 * Drawer latéral pour l'espace Administration
 * 
 * ✅ CORRIGÉ: Suppression des liens morts (Activité, Logs, Configuration)
 *    Ces fonctionnalités sont accessibles via la console Firebase.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  User,
  LayoutDashboard,
  Building2,
  Briefcase,
  Users,
  ArrowLeftRight,
  CreditCard,
  HelpCircle,
  LogOut,
  ChevronRight,
  Shield,
  ShieldAlert,
} from 'lucide-react';
import { useAuthStore, useUIStore } from '@/stores';

// ============================================================================
// TYPES
// ============================================================================

interface AdminMenuProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  badge?: number;
  disabled?: boolean;
}

interface MenuSectionProps {
  title: string;
  children: React.ReactNode;
}

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, onClick, badge, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <div className="flex items-center gap-3">
      <span className="text-gray-500">{icon}</span>
      <span className="text-gray-700 font-medium">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {badge !== undefined && badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <ChevronRight size={18} className="text-gray-400" />
    </div>
  </button>
);

const MenuSection: React.FC<MenuSectionProps> = ({ title, children }) => (
  <div className="py-2">
    <h3 className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</h3>
    <div className="space-y-1">{children}</div>
  </div>
);

const Divider: React.FC = () => <div className="border-t border-gray-200 my-2" />;

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export const AdminMenu: React.FC<AdminMenuProps> = ({ isOpen: isOpenProp, onClose: onCloseProp }) => {
  const navigate = useNavigate();
  
  // Utiliser les props si fournies, sinon le store
  const isMenuOpenStore = useUIStore(state => state.isMenuOpen);
  const closeMenuStore = useUIStore(state => state.closeMenu);
  
  const isOpen = isOpenProp !== undefined ? isOpenProp : isMenuOpenStore;
  const closeMenu = onCloseProp || closeMenuStore;
  
  const currentUser = useAuthStore(state => state.currentUser);
  const logout = useAuthStore(state => state.logout);

  const handleNavigate = (path: string) => { 
    closeMenu(); 
    navigate(path); 
  };
  
  const handleLogout = async () => { 
    closeMenu(); 
    await logout(); 
    navigate('/login'); 
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50 transition-opacity" onClick={closeMenu} />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 w-80 max-w-[85%] bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]">
          <button
            onClick={closeMenu}
            className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-center gap-3 mt-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Shield size={28} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-lg truncate">{currentUser?.name || 'Admin'}</h2>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                  Administrateur
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto">
          <MenuSection title="Administration">
            <MenuItem icon={<LayoutDashboard size={20} />} label="Dashboard" onClick={() => handleNavigate('/admin')} />
            <MenuItem icon={<Building2 size={20} />} label="Résidences" onClick={() => handleNavigate('/admin/residences')} />
            <MenuItem icon={<Briefcase size={20} />} label="Syndics" onClick={() => handleNavigate('/admin/syndics')} />
            <MenuItem icon={<Users size={20} />} label="Utilisateurs" onClick={() => handleNavigate('/admin/users')} />
            <MenuItem icon={<CreditCard size={20} />} label="Abonnements" onClick={() => handleNavigate('/admin/subscriptions')} />
            <MenuItem icon={<ShieldAlert size={20} />} label="Conformité plans" onClick={() => handleNavigate('/admin/compliance')} />
          </MenuSection>
          
          <Divider />
          
          <MenuSection title="Actions">
            <MenuItem icon={<ArrowLeftRight size={20} />} label="Transférer résidence" onClick={() => handleNavigate('/admin/transfer')} />
            <MenuItem icon={<Building2 size={20} />} label="Nouvelle résidence" onClick={() => handleNavigate('/admin/residences/new')} />
            <MenuItem icon={<Briefcase size={20} />} label="Nouveau syndic" onClick={() => handleNavigate('/admin/syndics/new')} />
          </MenuSection>
          
          <Divider />
          
          <MenuSection title="Aide">
            <MenuItem icon={<HelpCircle size={20} />} label="Support" onClick={() => handleNavigate('/support')} />
          </MenuSection>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium"
          >
            <LogOut size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
        
        <div className="text-center py-2 text-xs text-gray-400 safe-area-bottom">O'Copro Admin v1.0.0</div>
      </div>
    </>
  );
};

export default AdminMenu;
